import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
} from '@cca/ui';
import { RefreshCw } from 'lucide-react';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface Order {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  readonly financialStatus: string;
  readonly fulfilmentStatus: string;
  readonly currency: string;
  readonly totalPrice: string | null;
  readonly createdAt: string;
  readonly paidAt: string | null;
  readonly trackingNumbers: readonly (string | null)[];
}

interface SyncResource {
  readonly resource: string;
  readonly lastSyncedUpdatedAt: string | null;
  readonly lastRunAt: string | null;
  readonly lastRunStatus: string | null;
  readonly lastError: string | null;
}

interface SyncStatus {
  readonly simulated: boolean;
  readonly syncEnabled: boolean;
  readonly projection: { readonly orders: number; readonly customers: number };
  readonly stores: readonly {
    readonly id: string;
    readonly shopDomain: string;
    readonly shopName: string | null;
    readonly apiVersion: string;
    readonly installedAt: string | null;
    readonly uninstalledAt: string | null;
    readonly resources: readonly SyncResource[];
  }[];
}

interface SyncRunResult {
  readonly simulated: boolean;
  readonly results: readonly {
    readonly resource: string;
    readonly fetched: number;
    readonly written: number;
    readonly skippedStale: number;
    readonly complete: boolean;
  }[];
}

const FINANCIAL_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  PAID: 'success',
  PARTIALLY_PAID: 'warning',
  PENDING: 'warning',
  REFUNDED: 'neutral',
  PARTIALLY_REFUNDED: 'neutral',
  VOIDED: 'danger',
  UNKNOWN: 'neutral',
};

const FULFILMENT_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  FULFILLED: 'success',
  PARTIALLY_FULFILLED: 'warning',
  UNFULFILLED: 'warning',
  SCHEDULED: 'neutral',
  ON_HOLD: 'danger',
  CANCELLED: 'danger',
};

function formatMoney(amount: string | null, currency: string): string {
  if (!amount) return '—';
  const value = Number.parseFloat(amount);
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
}

function formatWhen(value: string | null): string {
  return value ? new Date(value).toLocaleString() : 'never';
}

/**
 * Sync state, shown above the orders rather than hidden in Settings.
 *
 * If the projection is stale, every number on this page is stale with it. The
 * page says when it last synced and whether the data came from a simulator, so
 * a reader is never left to assume it is live.
 */
function SyncPanel({ accessToken }: { readonly accessToken: string | null }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['sync-status'],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<SyncStatus>('/api/sync/status', { accessToken }),
  });

  const run = useMutation({
    mutationFn: (input: { storeId: string; full: boolean }) =>
      apiRequest<SyncRunResult>('/api/sync/run', {
        method: 'POST',
        accessToken,
        body: input,
      }),
    onSuccess: (result) => {
      const summary = result.results
        .map(
          (r) =>
            `${r.resource}: ${r.fetched} read, ${r.written} written, ${r.skippedStale} skipped` +
            (r.complete ? '' : ' (more remaining)'),
        )
        .join(' · ');
      setMessage(`${result.simulated ? 'Simulated sync. ' : ''}${summary}`);
      void queryClient.invalidateQueries({ queryKey: ['sync-status'] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (error) => {
      setMessage(error instanceof ApiError ? error.message : 'The sync could not be started.');
    },
  });

  if (status.isPending) return <LoadingState label="Loading sync status" />;
  if (status.isError) {
    return (
      <ErrorState
        description={
          status.error instanceof ApiError ? status.error.message : 'Could not load sync status.'
        }
        onRetry={() => void status.refetch()}
      />
    );
  }

  const data = status.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shopify sync</CardTitle>
        <CardDescription>
          {data.projection.orders} orders and {data.projection.customers} customers are projected
          locally. Everything below is read from that projection, not from Shopify directly.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.simulated ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            These orders come from the development simulator. No Shopify store is connected.
          </p>
        ) : null}

        {!data.syncEnabled ? (
          <p className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
            SHOPIFY_SYNC_ENABLED is false, so scheduled syncs do not run and this projection will
            not change on its own.
          </p>
        ) : null}

        {data.stores.length === 0 ? (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            No Shopify store is connected to this organisation.
          </p>
        ) : (
          <ul className="space-y-3">
            {data.stores.map((store) => (
              <li
                key={store.id}
                className="rounded-md border border-slate-200 p-3 dark:border-slate-800"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {store.shopName ?? store.shopDomain}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {store.shopDomain} · API {store.apiVersion}
                      {store.uninstalledAt ? ' · uninstalled' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={run.isPending || !data.syncEnabled}
                      onClick={() => run.mutate({ storeId: store.id, full: false })}
                    >
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                      Sync now
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={run.isPending || !data.syncEnabled}
                      onClick={() => run.mutate({ storeId: store.id, full: true })}
                      title="Ignore the watermark and re-read the whole store"
                    >
                      Full resync
                    </Button>
                  </div>
                </div>

                <dl className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {store.resources.length === 0 ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Never synced.</p>
                  ) : (
                    store.resources.map((resource) => (
                      <div key={resource.resource} className="text-xs">
                        <dt className="text-slate-500 dark:text-slate-400">
                          {resource.resource}
                          {resource.lastRunStatus ? (
                            <Badge
                              className="ml-1.5"
                              tone={
                                resource.lastRunStatus === 'complete'
                                  ? 'success'
                                  : resource.lastRunStatus === 'failed'
                                    ? 'danger'
                                    : 'warning'
                              }
                            >
                              {resource.lastRunStatus}
                            </Badge>
                          ) : null}
                        </dt>
                        <dd className="text-slate-700 dark:text-slate-300">
                          last run {formatWhen(resource.lastRunAt)}
                        </dd>
                        {resource.lastError ? (
                          <dd className="text-rose-700 dark:text-rose-400">{resource.lastError}</dd>
                        ) : null}
                      </div>
                    ))
                  )}
                </dl>
              </li>
            ))}
          </ul>
        )}

        {message ? (
          <p role="status" className="text-xs text-slate-700 dark:text-slate-300">
            {message}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function OrdersPage() {
  const accessToken = useAccessToken();
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [fulfilmentStatus, setFulfilmentStatus] = useState<string>('');

  const orders = useQuery({
    queryKey: ['orders', applied, fulfilmentStatus],
    enabled: Boolean(accessToken),
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (applied) params.set('query', applied);
      if (fulfilmentStatus) params.set('fulfilmentStatus', fulfilmentStatus);
      return apiRequest<{ orders: Order[]; total: number }>(`/api/orders?${params.toString()}`, {
        accessToken,
      });
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Orders</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          The local projection of Shopify orders. Read-only — nothing on this page changes an order
          in Shopify.
        </p>
      </header>

      <SyncPanel accessToken={accessToken} />

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault();
          setApplied(search.trim());
        }}
      >
        <label className="flex-1 text-sm">
          <span className="mb-1 block text-slate-700 dark:text-slate-300">
            Order number or email
          </span>
          <Input
            value={search}
            onChange={(changeEvent) => setSearch(changeEvent.target.value)}
            placeholder="#1044 or ana.popescu@example.test"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-700 dark:text-slate-300">Fulfilment</span>
          <select
            value={fulfilmentStatus}
            onChange={(changeEvent) => setFulfilmentStatus(changeEvent.target.value)}
            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="">Any</option>
            <option value="UNFULFILLED">Unfulfilled</option>
            <option value="PARTIALLY_FULFILLED">Partially fulfilled</option>
            <option value="FULFILLED">Fulfilled</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </label>
        <Button type="submit" size="sm">
          Search
        </Button>
      </form>

      <Card className="overflow-hidden p-0">
        {orders.isPending ? <LoadingState label="Loading orders" /> : null}

        {orders.isError ? (
          <ErrorState
            description={
              orders.error instanceof ApiError ? orders.error.message : 'Could not load orders.'
            }
            onRetry={() => void orders.refetch()}
          />
        ) : null}

        {orders.data && orders.data.orders.length === 0 ? (
          <EmptyState
            title="No orders match"
            description="Adjust the filters, or run a sync if the projection is empty."
          />
        ) : null}

        {orders.data && orders.data.orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Order
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Customer
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Payment
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Fulfilment
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Tracking
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    Total
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Placed
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.data.orders.map((order) => (
                  <tr
                    key={order.id}
                    className="border-b border-slate-100 last:border-0 dark:border-slate-800"
                  >
                    <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">
                      {order.name}
                    </td>
                    <td className="max-w-[16rem] truncate px-3 py-2 text-slate-700 dark:text-slate-300">
                      {order.email ?? '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={FINANCIAL_TONE[order.financialStatus] ?? 'neutral'}>
                        {order.financialStatus.toLowerCase().replaceAll('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={FULFILMENT_TONE[order.fulfilmentStatus] ?? 'neutral'}>
                        {order.fulfilmentStatus.toLowerCase().replaceAll('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                      {order.trackingNumbers.filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-800 tabular-nums dark:text-slate-200">
                      {formatMoney(order.totalPrice, order.currency)}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {orders.data ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Showing {orders.data.orders.length} of {orders.data.total} matching orders.
        </p>
      ) : null}
    </div>
  );
}

export const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  component: () => (
    <RequireSession>
      <OrdersPage />
    </RequireSession>
  ),
});
